import { Component } from "@theme/component";
import {
	ThemeEvents,
	VariantSelectedEvent,
	VariantUpdateEvent,
} from "@theme/events";
import { morph } from "@theme/morph";
import {
	requestYieldCallback,
	getViewParameterValue,
	shopifyFormatCurrency,
} from "@theme/utilities";

/**
 * @param {string | null | undefined} text
 * @returns {number}
 */
function parseMoneyTextToCents(text) {
	if (!text) return 0;
	const normalized = text
		.replace(/\u00a0/g, " ")
		.trim()
		.replace(/\s/g, "")
		.replace(/[^\d,.]/g, "")
		.replace(",", ".");
	const amount = Number.parseFloat(normalized);
	return Number.isNaN(amount) ? 0 : Math.round(amount * 100);
}

/**
 * @param {string} regularFormatted
 * @param {string} saleFormatted
 * @param {'full' | 'surcharge'} [priceMode]
 * @returns {string}
 */
function buildVariantSelectPricesHtml(
	regularFormatted,
	saleFormatted,
	priceMode = "full",
) {
	const saleLabel =
		priceMode === "surcharge" ? `+ ${saleFormatted}` : saleFormatted;
	const regularLabel =
		priceMode === "surcharge" ? `+ ${regularFormatted}` : regularFormatted;
	return `<span class="variant-option__select-prices"><s class="compare-at-price">${regularLabel}</s><span class="price sale-price">${saleLabel}</span></span>`;
}

/**
 * @param {string} html
 * @param {string} pricesHtml
 * @returns {string}
 */
function replaceSelectPricesHtml(html, pricesHtml) {
	if (!html) return pricesHtml;
	if (/<span class="variant-option__select-prices">[\s\S]*?<\/span>/.test(html)) {
		return html.replace(
			/<span class="variant-option__select-prices">[\s\S]*?<\/span>/,
			pricesHtml,
		);
	}
	if (html.includes("variant-option__select-text")) {
		return html.replace(
			/(<span class="variant-option__select-text">[\s\S]*?<\/span>)/,
			`$1${pricesHtml}`,
		);
	}
	return `${html}${pricesHtml}`;
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
	/** @type {string | undefined} */
	#pendingRequestUrl;

	/** @type {AbortController | undefined} */
	#abortController;

	/** @type {number[][]} */
	#checkedIndices = [];

	/** @type {any} */
	#currentBaseVariant = null;

	/** @type {HTMLInputElement[][]} */
	#radios = [];

	/** @type {MutationObserver | undefined} */
	#priceObserver;

	/** @type {number[]} */
	#discountRefreshTimeouts = [];

	/** @type {number | null} */
	#lastAppliedRatio = null;

	/** @type {((event: Event) => void) | undefined} */
	#variantUpdateHandler;

	/** @type {((event: Event) => void) | undefined} */
	#regiosPriceSettledHandler;

	connectedCallback() {
		super.connectedCallback();
		const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
			this.refs.fieldsets || []
		);

		for (const fieldset of fieldsets) {
			const radios = Array.from(fieldset?.querySelectorAll("input") ?? []);
			this.#radios.push(radios);

			const initialCheckedIndex = radios.findIndex(
				(radio) => radio.dataset.currentChecked === "true",
			);
			if (initialCheckedIndex !== -1) {
				this.#checkedIndices.push([initialCheckedIndex]);
			}
		}

		// Parse initial variant data
		try {
			const scriptTag = this.querySelector('script[type="application/json"]');
			if (scriptTag) {
				this.#currentBaseVariant = JSON.parse(scriptTag.textContent);
			}
		} catch (e) {
			console.error("Failed to parse variant JSON", e);
		}

		this.initCustomSelects();
		this.#observeMainPrice();
		this.#scheduleDiscountRefresh();
		this.addEventListener("change", this.variantChanged.bind(this));

		const closestSection = this.closest(".shopify-section, dialog");
		if (closestSection) {
			this.#variantUpdateHandler = (event) => this.#handleVariantUpdate(event);
			this.#regiosPriceSettledHandler = (event) =>
				this.#handleRegiosPriceSettled(event);
			closestSection.addEventListener(
				ThemeEvents.variantUpdate,
				this.#variantUpdateHandler,
			);
			closestSection.addEventListener(
				"regios-price-settled",
				this.#regiosPriceSettledHandler,
			);
		}
	}

	disconnectedCallback() {
		this.#priceObserver?.disconnect();
		for (const timeoutId of this.#discountRefreshTimeouts) {
			clearTimeout(timeoutId);
		}
		this.#discountRefreshTimeouts = [];

		const closestSection = this.closest(".shopify-section, dialog");
		if (closestSection) {
			if (this.#variantUpdateHandler) {
				closestSection.removeEventListener(
					ThemeEvents.variantUpdate,
					this.#variantUpdateHandler,
				);
			}
			if (this.#regiosPriceSettledHandler) {
				closestSection.removeEventListener(
					"regios-price-settled",
					this.#regiosPriceSettledHandler,
				);
			}
		}
	}

	/**
	 * @param {Event} event
	 */
	#handleVariantUpdate(event) {
		if (!(event instanceof VariantUpdateEvent)) return;
		if (event.target !== this) return;
		if (
			event.detail.data.productId &&
			event.detail.data.productId !== this.dataset.productId
		) {
			return;
		}

		this.#invalidateDiscountPrices();
	}

	/**
	 * @param {Event} event
	 */
	#handleRegiosPriceSettled(event) {
		const productId = this.dataset.productId;
		if (!productId) return;

		const productPrice = document.querySelector(
			`product-price[data-product-id="${productId}"]`,
		);
		if (!productPrice?.contains(event.target)) return;

		this.#invalidateDiscountPrices();
	}

	#invalidateDiscountPrices() {
		this.#lastAppliedRatio = null;
		this.#observeMainPrice();
		this.#scheduleDiscountRefresh();
	}

	/**
	 * Handles the variant change event.
	 * @param {Event} event - The variant change event.
	 */
	variantChanged(event) {
		if (!(event.target instanceof HTMLElement)) return;

		// Ignore synthetic change events on listing cards (e.g. during custom element upgrade).
		if (
			this.closest("product-card") &&
			event instanceof Event &&
			!event.isTrusted
		) {
			return;
		}

		const selectedOption =
			event.target instanceof HTMLSelectElement
				? event.target.options[event.target.selectedIndex]
				: event.target;

		if (!selectedOption) return;

		this.updateSelectedOption(event.target);

		// We want to update the price even if it's an addon change
		if (event.target.hasAttribute("data-addon-select")) {
			if (this.shouldUpdateTotalPrice()) {
				this.updateTotalPrice();
			}
			return;
		}

		this.dispatchEvent(
			new VariantSelectedEvent({
				id: selectedOption.dataset.optionValueId ?? "",
			}),
		);

		const isOnProductPage =
			this.dataset.templateProductMatch === "true" &&
			!event.target.closest("product-card") &&
			!event.target.closest("quick-add-dialog");

		// Morph the entire main content for combined listings child products, because changing the product
		// might also change other sections depending on recommendations, metafields, etc.
		const currentUrl = this.dataset.productUrl?.split("?")[0];
		const newUrl = selectedOption.dataset.connectedProductUrl;
		const loadsNewProduct =
			isOnProductPage && !!newUrl && newUrl !== currentUrl;

		this.fetchUpdatedSection(
			this.buildRequestUrl(selectedOption),
			loadsNewProduct,
		);

		const url = new URL(window.location.href);

		const variantId = selectedOption.dataset.variantId || null;

		if (isOnProductPage) {
			if (variantId) {
				url.searchParams.set("variant", variantId);
			} else {
				url.searchParams.delete("variant");
			}
		}

		// Change the path if the option is connected to another product via combined listing.
		if (loadsNewProduct) {
			url.pathname = newUrl;
		}

		if (url.href !== window.location.href) {
			requestYieldCallback(() => {
				history.replaceState({}, "", url.toString());
			});
		}
	}

	/**
	 * Updates the selected option.
	 * @param {string | Element} targetParam - The target element.
	 */
	updateSelectedOption(targetParam) {
		let target = targetParam;
		if (typeof target === "string") {
			const targetElement = this.querySelector(
				`[data-option-value-id="${target}"]`,
			);

			if (!targetElement) throw new Error("Target element not found");

			target = targetElement;
		}

		if (target instanceof HTMLInputElement) {
			const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || "");
			const inputIndex = Number.parseInt(target.dataset.inputIndex || "");

			if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
				const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
					this.refs.fieldsets || []
				);
				const fieldset = fieldsets[fieldsetIndex];
				const checkedIndices = this.#checkedIndices[fieldsetIndex];
				const radios = this.#radios[fieldsetIndex];

				if (radios && checkedIndices && fieldset) {
					// Clear previous checked states
					const [currentIndex, previousIndex] = checkedIndices;

					if (currentIndex !== undefined && radios[currentIndex]) {
						radios[currentIndex].dataset.previousChecked = "false";
					}
					if (previousIndex !== undefined && radios[previousIndex]) {
						radios[previousIndex].dataset.previousChecked = "false";
					}

					// Update checked indices array - keep only the last 2 selections
					checkedIndices.unshift(inputIndex);
					checkedIndices.length = Math.min(checkedIndices.length, 2);

					// Update the new states
					const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
					const newPreviousIndex = checkedIndices[1]; // This might be undefined

					// newCurrentIndex is guaranteed to exist since we just added it
					if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
						radios[newCurrentIndex].dataset.currentChecked = "true";
						fieldset.style.setProperty(
							"--pill-width-current",
							`${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`,
						);
					}

					if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
						radios[newPreviousIndex].dataset.previousChecked = "true";
						radios[newPreviousIndex].dataset.currentChecked = "false";
						fieldset.style.setProperty(
							"--pill-width-previous",
							`${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`,
						);
					}
				}
			}
			target.checked = true;
		}

		if (target instanceof HTMLSelectElement) {
			const newValue = target.value;
			const newSelectedOption = Array.from(target.options).find(
				(option) => option.value === newValue,
			);

			if (!newSelectedOption) throw new Error("Option not found");

			for (const option of target.options) {
				option.removeAttribute("selected");
			}

			newSelectedOption.setAttribute("selected", "selected");
		}
	}

	/**
	 * Builds the request URL.
	 * @param {HTMLElement} selectedOption - The selected option.
	 * @param {string | null} [source] - The source.
	 * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
	 * @returns {string} The request URL.
	 */
	buildRequestUrl(
		selectedOption,
		source = null,
		sourceSelectedOptionsValues = [],
	) {
		// this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
		// We create a new URL and abort the previous fetch request if it's still pending.
		let productUrl =
			selectedOption.dataset.connectedProductUrl ||
			this.#pendingRequestUrl ||
			this.dataset.productUrl;
		this.#pendingRequestUrl = productUrl;
		const params = [];
		const viewParamValue = getViewParameterValue();

		// preserve view parameter, if it exists, for alternative product view testing
		if (viewParamValue) params.push(`view=${viewParamValue}`);

		if (this.selectedOptionsValues.length && !source) {
			params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
		} else if (source === "product-card") {
			if (this.selectedOptionsValues.length) {
				params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
			} else {
				params.push(`option_values=${selectedOption.dataset.optionValueId}`);
			}
		}

		// If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
		if (
			this.closest("quick-add-component") ||
			this.closest("swatches-variant-picker-component")
		) {
			if (productUrl?.includes("?")) {
				productUrl = productUrl.split("?")[0];
			}
			return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
		}
		return `${productUrl}?${params.join("&")}`;
	}

	/**
	 * Fetches the updated section.
	 * @param {string} requestUrl - The request URL.
	 * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
	 */
	fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
		// We use this to abort the previous fetch request if it's still pending.
		this.#abortController?.abort();
		this.#abortController = new AbortController();

		fetch(requestUrl, { signal: this.#abortController.signal })
			.then((response) => response.text())
			.then((responseText) => {
				this.#pendingRequestUrl = undefined;
				const html = new DOMParser().parseFromString(responseText, "text/html");
				// Defer is only useful for the initial rendering of the page. Remove it here.
				html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

				const textContent = html.querySelector(
					`${this.tagName.toLowerCase()} script[type="application/json"]`,
				)?.textContent;
				if (!textContent) return;

				if (shouldMorphMain) {
					this.updateMain(html);
				} else {
					const newProduct = this.updateVariantPicker(html);

					// We grab the variant object from the response and dispatch an event with it.
					if (this.selectedOptionId) {
						this.dispatchEvent(
							new VariantUpdateEvent(
								JSON.parse(textContent),
								this.selectedOptionId,
								{
									html,
									productId: this.dataset.productId ?? "",
									newProduct,
								},
							),
						);

						this.#currentBaseVariant = JSON.parse(textContent);
						if (this.shouldUpdateTotalPrice()) {
							this.updateTotalPrice();
						}
					}
				}
			})
			.catch((error) => {
				if (error.name === "AbortError") {
					console.warn("Fetch aborted by user");
				} else {
					console.error(error);
				}
			});
	}

	/**
	 * @typedef {Object} NewProduct
	 * @property {string} id
	 * @property {string} url
	 */

	/**
	 * Re-renders the variant picker.
	 * @param {Document} newHtml - The new HTML.
	 * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
	 */
	updateVariantPicker(newHtml) {
		/** @type {NewProduct | undefined} */
		let newProduct;

		const newVariantPickerSource = newHtml.querySelector(
			this.tagName.toLowerCase(),
		);

		if (!newVariantPickerSource) {
			throw new Error("No new variant picker source found");
		}

		// For combined listings, the product might have changed, so update the related data attribute.
		if (newVariantPickerSource instanceof HTMLElement) {
			const newProductId = newVariantPickerSource.dataset.productId;
			const newProductUrl = newVariantPickerSource.dataset.productUrl;

			if (
				newProductId &&
				newProductUrl &&
				this.dataset.productId !== newProductId
			) {
				newProduct = { id: newProductId, url: newProductUrl };
			}

			this.dataset.productId = newProductId;
			this.dataset.productUrl = newProductUrl;
		}

		morph(this, newVariantPickerSource);
		this.initCustomSelects();

		return newProduct;
	}

	/**
	 * Re-renders the entire main content.
	 * @param {Document} newHtml - The new HTML.
	 */
	updateMain(newHtml) {
		const main = document.querySelector("main");
		const newMain = newHtml.querySelector("main");

		if (!main || !newMain) {
			throw new Error("No new main source found");
		}

		morph(main, newMain);
		this.initCustomSelects();
	}

	/**
	 * Gets the selected option.
	 * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
	 */
	get selectedOption() {
		const selectedOption = this.querySelector(
			"select option[selected], fieldset input:checked",
		);

		if (
			!(
				selectedOption instanceof HTMLInputElement ||
				selectedOption instanceof HTMLOptionElement
			)
		) {
			return undefined;
		}

		return selectedOption;
	}

	/**
	 * Gets the selected option ID.
	 * @returns {string | undefined} The selected option ID.
	 */
	get selectedOptionId() {
		const { selectedOption } = this;
		if (!selectedOption) return undefined;
		const { optionValueId } = selectedOption.dataset;

		if (!optionValueId) {
			throw new Error("No option value ID found");
		}

		return optionValueId;
	}

	/**
	 * Gets the selected options values.
	 * @returns {string[]} The selected options values.
	 */
	get selectedOptionsValues() {
		/** @type HTMLElement[] */
		const selectedOptions = Array.from(
			this.querySelectorAll("select option[selected], fieldset input:checked"),
		);

		return selectedOptions.map((option) => {
			const { optionValueId } = option.dataset;

			if (!optionValueId) throw new Error("No option value ID found");

			return optionValueId;
		});
	}

	initCustomSelects() {
		this.#lastAppliedRatio = null;

		for (const select of this.querySelectorAll("select")) {
			const existingWrapper = select.nextElementSibling;
			if (existingWrapper?.classList.contains("selectbox_wrapper")) {
				existingWrapper.remove();
				select.style.display = "";
			}
			this.createCustomSelect(select);
		}
		this.#observeMainPrice();
		this.#applyDiscountFromMainPrice();
	}

	/**
	 * @returns {number | null} Sale price / regular price, or null if no campaign discount.
	 */
	#getDiscountRatioFromMainPrice() {
		const productId = this.dataset.productId;
		if (!productId) return null;

		const container = document.querySelector(
			`product-price[data-product-id="${productId}"] [ref="priceContainer"]`,
		);
		if (!container) return null;

		let compareEl = container.querySelector(".compare-at-price");
		let saleEl =
			container.querySelector(".sale-price") ||
			container.querySelector(".price:not(.compare-at-price)");

		if (container.classList.contains("regios-dopp-generic-price--on-sale")) {
			if (!compareEl) {
				compareEl = container.querySelector(
					"s, del, .regios-dopp-generic-compare-at-price",
				);
			}
			if (!saleEl) {
				saleEl = container.querySelector(
					".regios-dopp-generic-price--on-sale > .price, .regios-dopp-generic-price--on-sale .price",
				);
			}
			const priceEls = container.querySelectorAll(".price");
			if (!compareEl && priceEls.length >= 2) {
				compareEl = priceEls[0];
				saleEl = priceEls[priceEls.length - 1];
			}
		}

		if (!compareEl || !saleEl) return null;

		const regularCents = parseMoneyTextToCents(compareEl.textContent);
		const saleCents = parseMoneyTextToCents(saleEl.textContent);
		if (!regularCents || !saleCents || saleCents >= regularCents) return null;

		return saleCents / regularCents;
	}

	#observeMainPrice() {
		const productId = this.dataset.productId;
		if (!productId) return;

		const productPrice = document.querySelector(
			`product-price[data-product-id="${productId}"]`,
		);
		if (!productPrice) return;

		this.#priceObserver?.disconnect();
		this.#priceObserver = new MutationObserver(() => {
			this.#lastAppliedRatio = null;
			this.#applyDiscountFromMainPrice();
		});
		this.#priceObserver.observe(productPrice, {
			childList: true,
			subtree: true,
			characterData: true,
			attributes: true,
			attributeFilter: ["class"],
		});
	}

	#scheduleDiscountRefresh() {
		for (const timeoutId of this.#discountRefreshTimeouts) {
			clearTimeout(timeoutId);
		}
		this.#discountRefreshTimeouts = [];

		const delays = [0, 100, 400, 1200, 3000, 6000];
		for (const delay of delays) {
			const timeoutId = window.setTimeout(() => {
				this.#applyDiscountFromMainPrice();
			}, delay);
			this.#discountRefreshTimeouts.push(timeoutId);
		}
	}

	#applyDiscountFromMainPrice() {
		const ratio = this.#getDiscountRatioFromMainPrice();
		if (ratio == null) return;
		if (this.#lastAppliedRatio === ratio) return;

		const format = this.dataset.moneyFormat || "{{amount}}";

		for (const select of this.querySelectorAll("select")) {
			const priceMode =
				select.dataset.addonPriceMode === "surcharge" ? "surcharge" : "full";

			for (const option of select.options) {
				const variantPrice = option.dataset.variantPrice;
				if (!variantPrice) continue;

				const cents = Number(variantPrice);
				if (!cents) continue;

				const saleCents = Math.round(cents * ratio);
				const regularFormatted = shopifyFormatCurrency(cents, format);
				const saleFormatted = shopifyFormatCurrency(saleCents, format);
				const pricesHtml = buildVariantSelectPricesHtml(
					regularFormatted,
					saleFormatted,
					priceMode,
				);
				const newHtml = replaceSelectPricesHtml(
					option.dataset.html || "",
					pricesHtml,
				);
				option.dataset.html = newHtml;

				const wrapper = select.nextElementSibling;
				if (!wrapper?.classList.contains("selectbox_wrapper")) continue;

				const listItem = wrapper.querySelector(
					`.selectbox_list_item[data-value="${CSS.escape(option.value)}"]`,
				);
				if (listItem) listItem.innerHTML = newHtml;

				if (option.selected) {
					const span = wrapper.querySelector(".selectbox_input_text");
					if (span) span.innerHTML = newHtml;
				}
			}
		}

		this.#lastAppliedRatio = ratio;
	}

	/**
	 * Creates a custom select.
	 * @param {HTMLSelectElement} select - The select element.
	 */
	createCustomSelect(select) {
		// Hide original select
		select.style.display = "none";

		// Create wrapper
		const wrapper = document.createElement("div");
		wrapper.className = "selectbox_wrapper";

		// Create input/display
		const input = document.createElement("button");
		input.type = "button";
		input.className = "selectbox_input variant-option__select";
		// Add arrow icon using the existing icon-caret from the liquid file
		const selectedOpt = select.options[select.selectedIndex];
		input.innerHTML = `<span class="selectbox_input_text">${selectedOpt?.dataset.html || selectedOpt?.innerHTML || ""}</span>`;

		// Create list
		const list = document.createElement("ul");
		list.className = "selectbox_list";

		const option_list = Array.from(select.options).filter(
			(option) => option.value !== "",
		);

		// Populate list options
		for (const option of option_list) {
			const item = document.createElement("li");
			item.className = `selectbox_list_item ${
				option.selected ? "selected" : ""
			} ${option.disabled ? "disabled" : ""}`;
			item.innerHTML = option.dataset.html || option.innerHTML;
			item.dataset.value = option.value;
			if (option.dataset.optionValueId) {
				item.dataset.optionValueId = option.dataset.optionValueId;
			}

			if (!option.disabled) {
				item.addEventListener("click", (e) => {
					e.stopPropagation();
					// Update select value
					select.value = option.value;
					select.dispatchEvent(new Event("change", { bubbles: true }));

					// Update visual state
					const span = input.querySelector("span");
					if (span) {
						span.innerHTML = option.dataset.html || option.innerHTML;
					}
					for (const item of list.querySelectorAll(".selectbox_list_item")) {
						item.classList.remove("selected");
					}
					item.classList.add("selected");

					wrapper.classList.remove("selectbox_open");
				});
			}

			list.appendChild(item);
		}

		wrapper.appendChild(input);
		wrapper.appendChild(list);

		// Insert after the select's parent (.variant-option__select-wrapper)
		select.parentNode?.insertBefore(wrapper, select.nextSibling);

		// If only one option, disable dropdown interaction
		if (option_list.length <= 1) {
			wrapper.classList.add("selectbox_single");
			return;
		}

		// close if click outside
		wrapper.addEventListener("click", (e) => {
			e.stopPropagation();
			for (const otherWrapper of document.querySelectorAll(".selectbox_open")) {
				if (otherWrapper !== wrapper) {
					otherWrapper.classList.remove("selectbox_open");
				}
			}
			const willOpen = !wrapper.classList.contains("selectbox_open");
			wrapper.classList.toggle("selectbox_open");
			if (willOpen && this.#lastAppliedRatio == null) {
				this.#applyDiscountFromMainPrice();
			}
		});

		// Close on click outside
		document.addEventListener("click", (e) => {
			if (e.target instanceof Node && !wrapper.contains(e.target)) {
				wrapper.classList.remove("selectbox_open");
			}
		});
	}

	updateTotalPrice() {
		if (!this.shouldUpdateTotalPrice()) return;

		if (!this.#currentBaseVariant) return;

		let totalPrice = this.#currentBaseVariant.price;

		const addonSelects = /** @type {NodeListOf<HTMLSelectElement>} */ (
			this.querySelectorAll("[data-addon-select]")
		);
		for (const select of addonSelects) {
			const selectedOption = select.options[select.selectedIndex];
			if (selectedOption && selectedOption.dataset.price) {
				const price = Number(selectedOption.dataset.price);
				if (!Number.isNaN(price)) {
					totalPrice += price;
				}
			}
		}

		const scope = this.closest(".shopify-section, dialog");
		if (!scope) return;

		const priceContainers = scope.querySelectorAll(
			`product-price[data-product-id="${this.dataset.productId}"] [ref="priceContainer"]`,
		);
		const priceContainer = Array.from(priceContainers).find(
			(container) => !container.closest("product-card"),
		);

		if (!priceContainer) return;

		if (
			priceContainer instanceof HTMLElement &&
			priceContainer.classList.contains("price-container--awaiting-regios") &&
			!priceContainer.classList.contains("price-container--regios-ready")
		) {
			return;
		}

		const priceElement = priceContainer.querySelector(".price");

		if (priceElement) {
			const format = this.dataset.moneyFormat || "{{amount}}";
			const formatMoney = window.Shopify?.formatMoney;

			priceElement.innerHTML = formatMoney
				? formatMoney(totalPrice, format)
				: shopifyFormatCurrency(totalPrice, format);
		}
	}

	shouldUpdateTotalPrice() {
		// Only the main PDP variant picker (addons) should recalculate price.
		// Product card swatches must never call updateTotalPrice().
		if (this.closest("product-card, product-card-link")) return false;

		return this.dataset.templateProductMatch === "true";
	}
}

if (!customElements.get("variant-picker")) {
	customElements.define("variant-picker", VariantPicker);
}
